import { call, put, takeEvery, takeLatest, select, delay } from "redux-saga/effects";
import {
  SG_GET_ORGANIZATION_GROUP,
  SG_FETCH_ORGANIZATION_GROUP,
  SG_ADD_ORGANIZATION_GROUP,
  SG_EDIT_ORGANIZATION_GROUP,
  SG_DELETE_ORGANIZATION_GROUP,
  SG_BULK_UPDATE_ORGANIZATION_GROUP,
  SG_ADD_ORGANIZATION_GROUP_MEMBER,
  SG_REMOVE_ORGANIZATION_GROUP_MEMBER,
  SG_ORG_GROUP_ADD_BY_CATEGORY,
  SG_ORG_GROUP_REMOVE_BY_CATEGORY,
  ORGANIZATION_GROUP_CATEGORY_PREVIEW,
  ORGANIZATION_GROUP_CATEGORY_CONFIRMED,
  GET_ORGANIZATION_GROUP,
  FETCH_ORGANIZATION_GROUP,
  UPDATE_ORGANIZATION_GROUP,
  DELETE_ORGANIZATION_GROUP,
  BULK_UPDATE_ORGANIZATION_GROUP,
  ADD_ORGANIZATION_GROUP_MEMBER,
  REMOVE_ORGANIZATION_GROUP_MEMBER,
  PENDING_ORGANIZATION_GROUP,
  CLEAR_ORGANIZATION_GROUP,
} from "../constants/actions";

import {
  GET_ORGANIZATION_GROUP as GET_ORGANIZATION_GROUP_API,
  FETCH_ORGANIZATION_GROUP as FETCH_ORGANIZATION_GROUP_API,
  ADD_ORGANIZATION_GROUP as ADD_ORGANIZATION_GROUP_API,
  EDIT_ORGANIZATION_GROUP as EDIT_ORGANIZATION_GROUP_API,
  DELETE_ORGANIZATION_GROUP as DELETE_ORGANIZATION_GROUP_API,
  BULK_UPDATE_ORGANIZATION_GROUP as BULK_UPDATE_ORGANIZATION_GROUP_API,
  ADD_ORGANIZATION_GROUP_MEMBER as ADD_ORGANIZATION_GROUP_MEMBER_API,
  REMOVE_ORGANIZATION_GROUP_MEMBER as REMOVE_ORGANIZATION_GROUP_MEMBER_API,
  ADD_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY,
  REMOVE_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY,
} from "../constants/api";

// Get Organization Groups
function* getOrganizationGroup(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    console.log('🔥 SAGA: Fetching organization groups with payload:', action.payload);
    const payload = action?.payload;

    // Some screens (AutomatedReports) need the full permission-group list (can exceed 5000).
    // If we detect a "large page_size" request for permission groups, page through and merge.
    const canPaginate =
      payload &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      (payload.group_type === "permission" || payload.group_type === "survey") &&
      Number(payload.page_size) >= 5000;

    let response = null;
    if (!canPaginate) {
      response = yield call(GET_ORGANIZATION_GROUP_API, payload);
    } else {
      const merged = [];
      const maxTotal = 25000;
      const maxPages = 20;
      let page = Number(payload.page) > 0 ? Number(payload.page) : 1;
      let next = true;
      let pages = 0;

      while (next && merged.length < maxTotal && pages < maxPages) {
        const pagePayload = { ...payload, page };
        // eslint-disable-next-line no-await-in-loop
        const resp = yield call(GET_ORGANIZATION_GROUP_API, pagePayload);
        response = resp;
        const data = resp?.data;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        merged.push(...results);

        const hasNext = !!(data?.next || data?.links?.next);
        next = hasNext && results.length > 0;
        page += 1;
        pages += 1;

        // Small delay to avoid spamming the API when paging large lists.
        if (next) {
          yield delay(180);
        }
      }

      if (response?.data && typeof response.data === "object" && !Array.isArray(response.data)) {
        response = {
          ...response,
          data: {
            ...response.data,
            results: merged,
            next: null,
            previous: null,
          },
        };
      } else {
        response = { ...response, data: merged };
      }
    }

    console.log('🔥 SAGA: API Response:', response?.data);
    console.log('🔥 SAGA: Pagination fields - count:', response?.data?.count, 'next:', response?.data?.next, 'previous:', response?.data?.previous);
    yield put({
      type: GET_ORGANIZATION_GROUP,
      payload: response?.data,
      meta: {
        group_type: action?.payload?.group_type,
        organization: action?.payload?.organization,
        page_size: action?.payload?.page_size,
      },
    });
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    console.error("Error fetching organization groups:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Fetch Single Organization Group
function* fetchOrganizationGroup(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    const response = yield call(FETCH_ORGANIZATION_GROUP_API, action.payload);
    yield put({ type: FETCH_ORGANIZATION_GROUP, payload: response.data });
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    console.error("Error fetching organization group:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Add Organization Group
function* addOrganizationGroup(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    const response = yield call(ADD_ORGANIZATION_GROUP_API, action.payload);
    
    // Store the newly created group in state.organization_group.organization_group
    yield put({ type: UPDATE_ORGANIZATION_GROUP, payload: response.data });
    
    // Refresh the list after successful creation
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'survey' } });
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    console.error("Error adding organization group:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Edit Organization Group
function* editOrganizationGroup(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    const response = yield call(EDIT_ORGANIZATION_GROUP_API, action.payload);
    
    // Refresh the list after successful edit
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'permission' } });
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    console.error("Error editing organization group:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Delete Organization Group
function* deleteOrganizationGroup(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    yield call(DELETE_ORGANIZATION_GROUP_API, action.payload);
    
    // Refresh the list after successful deletion
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'permission' } });
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    console.error("Error deleting organization group:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Test function to catch ANY bulk update action
function* testBulkAction(action) {
  console.log('🧪 TEST SAGA: Any bulk action caught:', action.type);
  console.log('🧪 TEST SAGA: Action:', action);
}

// Bulk Update Organization Group (Add/Remove multiple employees)
function* bulkUpdateOrganizationGroup(action) {
  console.log('🔥 SAGA: bulkUpdateOrganizationGroup called with action:', action);
  console.log('🔥 SAGA: Payload:', action.payload);
  
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    
    console.log('🔥 SAGA: About to call BULK_UPDATE_ORGANIZATION_GROUP_API...');
    console.log('🔥 SAGA: API function:', BULK_UPDATE_ORGANIZATION_GROUP_API);
    
    const response = yield call(BULK_UPDATE_ORGANIZATION_GROUP_API, action.payload);
    console.log('🔥 SAGA: API Response received:', response);
    
    // Include the organization_group_id in the payload so reducer knows which group finished
    const bulkPayload = {
      ...response.data,
      organization_group_id: action.payload.organization_group_id
    };
    yield put({ type: BULK_UPDATE_ORGANIZATION_GROUP, payload: bulkPayload });
    
    // Refresh organization groups to show updated data
    console.log('🔥 SAGA: Refreshing organization groups...');
    // Note: This will refresh to page 1, which is expected behavior after bulk operations
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'permission' } });

    // Also fetch the updated single group so detail views can refresh regardless of pagination
    if (action?.payload?.organization_group_id) {
      yield put({ type: SG_FETCH_ORGANIZATION_GROUP, payload: { id: action.payload.organization_group_id } });
    }
    
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
    console.log('🔥 SAGA: Bulk update completed successfully');
  } catch (error) {
    console.error("❌ SAGA: Error bulk updating organization group:", error);
    console.error("❌ SAGA: Error details:", error.response?.data);
    console.error("❌ SAGA: Error status:", error.response?.status);
    console.error("❌ SAGA: Full error object:", error);
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
    
    // Don't throw the error to prevent saga from crashing
    console.log("❌ SAGA: Continuing execution despite error");
  }
}

// Add Organization Group Member
function* addOrganizationGroupMember(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    const response = yield call(ADD_ORGANIZATION_GROUP_MEMBER_API, action.payload);
    
    // Include the group_id in the payload so reducer knows which group was affected
    const memberPayload = {
      ...response.data,
      group_id: action.payload.group_id
    };
    yield put({ type: ADD_ORGANIZATION_GROUP_MEMBER, payload: memberPayload });
    
    // Refresh organization groups and single group to show updated data
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'survey' } });

    if (action?.payload?.group_id) {
      yield put({ type: SG_FETCH_ORGANIZATION_GROUP, payload: { id: action.payload.group_id } });
    }
    
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Remove Organization Group Member
function* removeOrganizationGroupMember(action) {
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    
    const response = yield call(REMOVE_ORGANIZATION_GROUP_MEMBER_API, action.payload);
    
    // Include the group_id in the payload so reducer knows which group was affected
    const memberPayload = {
      ...response.data,
      group_id: action.payload.group_id
    };
    yield put({ type: REMOVE_ORGANIZATION_GROUP_MEMBER, payload: memberPayload });
    
    // Refresh organization groups and single group to show updated data
    const orgId = yield select((state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id);
    yield put({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: 12, organization: orgId, group_type: 'survey' } });

    if (action?.payload?.group_id) {
      yield put({ type: SG_FETCH_ORGANIZATION_GROUP, payload: { id: action.payload.group_id } });
    }
    
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  } catch (error) {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
    
  }
}

// Add Organization Group Members By Category
function* addMembersByCategory(action) {
  const {
    organization_group_id,
    // New preferred API: array of category filter objects
    category_filters,
    // Legacy support: a single filters object or pre-built category_filters
    filters = {},
    confirm = false,
    remove_tokens = false,
  } = action.payload || {};

  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });

    // Normalize into the array shape expected by the backend
    let normalizedCategoryFilters = category_filters;

    if (!Array.isArray(normalizedCategoryFilters)) {
      if (Array.isArray(filters)) {
        normalizedCategoryFilters = filters;
      } else if (filters && Object.keys(filters).length > 0) {
        // Backward-compat: map a single filters object into a one-element array
        normalizedCategoryFilters = [filters];
      } else {
        normalizedCategoryFilters = [];
      }
    }

    const body = {
      category_filters: normalizedCategoryFilters,
      // Explicitly send confirm/remove_tokens flags so backend knows the stage
      ...(confirm ? { confirm: true } : {}),
      ...(remove_tokens ? { remove_tokens: true } : {}),
    };

    const response = yield call(ADD_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY, {
      organization_group_id,
      body,
    });
    const data = response?.data || {};
    const mode = "add";

    if (data.stage === "preview" || !confirm) {
      // Stage 1: preview
      yield put({
        type: ORGANIZATION_GROUP_CATEGORY_PREVIEW,
        payload: {
          organization_group_id,
          mode,
          remove_tokens: false,
          data,
        },
      });
    } else {
      // Stage 2: confirmed
      yield put({
        type: ORGANIZATION_GROUP_CATEGORY_CONFIRMED,
        payload: {
          organization_group_id,
          mode,
          remove_tokens: false,
          data,
        },
      });
      // On confirmed add, refresh group lists and detail,
      // reusing the pattern from bulkUpdateOrganizationGroup
      const orgId = yield select(
        (state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id
      );
      // Refresh list (permissions group type is what existing bulk saga uses)
      yield put({
        type: SG_GET_ORGANIZATION_GROUP,
        payload: {
          page: 1,
          page_size: 12,
          organization: orgId,
          group_type: "permission",
        },
      });
      // Refresh single group detail
      if (organization_group_id) {
        yield put({
          type: SG_FETCH_ORGANIZATION_GROUP,
          payload: { id: organization_group_id },
        });
      }
    }
  } catch (error) {
    console.error("❌ SAGA: Error in addMembersByCategory:", error);
  } finally {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

// Remove Organization Group Members By Category
function* removeMembersByCategory(action) {
  const {
    organization_group_id,
    filters = {},
    confirm = false,
    remove_tokens = false,
  } = action.payload || {};
  try {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: true });
    const body = {
      ...filters,
      ...(confirm ? { confirm: true } : {}),
      ...(remove_tokens ? { remove_tokens: true } : {}),
    };
    const response = yield call(REMOVE_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY, {
      organization_group_id,
      body,
    });
    const data = response?.data || {};
    const mode = "remove";
    if (data.stage === "preview" || !confirm) {
      // Stage 1: preview
      yield put({
        type: ORGANIZATION_GROUP_CATEGORY_PREVIEW,
        payload: {
          organization_group_id,
          mode,
          remove_tokens,
          data,
        },
      });
    } else {
      // Stage 2: confirmed
      yield put({
        type: ORGANIZATION_GROUP_CATEGORY_CONFIRMED,
        payload: {
          organization_group_id,
          mode,
          remove_tokens,
          data,
        },
      });
      // Refresh list and detail after confirmed removal
      const orgId = yield select(
        (state) => state.auth?.organization_id || state.auth?.selectedOrganization?.id
      );
      yield put({
        type: SG_GET_ORGANIZATION_GROUP,
        payload: {
          page: 1,
          page_size: 12,
          organization: orgId,
          group_type: "permission",
        },
      });
      if (organization_group_id) {
        yield put({
          type: SG_FETCH_ORGANIZATION_GROUP,
          payload: { id: organization_group_id },
        });
      }
    }
  } catch (error) {
    console.error("❌ SAGA: Error in removeMembersByCategory:", error);
  } finally {
    yield put({ type: PENDING_ORGANIZATION_GROUP, payload: false });
  }
}

export default function* OrganizationGroupSaga() {
  console.log('🔥 SAGA SETUP: OrganizationGroup saga watchers registered');
  console.log('🔥 SAGA SETUP: SG_BULK_UPDATE_ORGANIZATION_GROUP constant value:', SG_BULK_UPDATE_ORGANIZATION_GROUP);
  
  yield takeEvery(SG_GET_ORGANIZATION_GROUP, getOrganizationGroup);
  yield takeEvery(SG_FETCH_ORGANIZATION_GROUP, fetchOrganizationGroup);
  yield takeEvery(SG_ADD_ORGANIZATION_GROUP, addOrganizationGroup);
  yield takeEvery(SG_EDIT_ORGANIZATION_GROUP, editOrganizationGroup);
  yield takeEvery(SG_DELETE_ORGANIZATION_GROUP, deleteOrganizationGroup);
  yield takeEvery(SG_BULK_UPDATE_ORGANIZATION_GROUP, bulkUpdateOrganizationGroup);
  yield takeEvery(SG_ADD_ORGANIZATION_GROUP_MEMBER, addOrganizationGroupMember);
  yield takeEvery(SG_REMOVE_ORGANIZATION_GROUP_MEMBER, removeOrganizationGroupMember);
  yield takeEvery(SG_ORG_GROUP_ADD_BY_CATEGORY, addMembersByCategory);
  yield takeEvery(SG_ORG_GROUP_REMOVE_BY_CATEGORY, removeMembersByCategory);
  
  // Test watcher for any action containing "BULK"
  yield takeEvery((action) => action.type && action.type.includes('BULK'), testBulkAction);
}